import { memo, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import cn from 'classnames';
import { Spinner ,
  Avatar,
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
} from '@heroui/react';
import { CHAT_MESSAGES_SCROLL_TOP_THRESHOLD_PX } from '../../config/constants';
import { useNearScrollTop } from '../../lib/useNearScrollTop';
import type { ChatAttachment, ChatMessage, ChatThread } from '../../model/types';
import { ChatMediaViewerModal } from '../ChatMediaViewerModal/ChatMediaViewerModal';
import { MessageBubble } from '../MessageBubble/MessageBubble';
import { ChatComposer } from '../ChatComposer/ChatComposer';
import { ChatPinnedMessages } from '../ChatPinnedMessages/ChatPinnedMessages';
import { IconArchive, IconInfoCircle, IconKebab, IconLeave, IconLock, IconSettings, IconTrash } from '../icons';
import { isMessagePinned } from '../../lib/pinnedMessages';

export type ChatConversationProps = {
  thread: ChatThread | null;
  messages: ChatMessage[];
  draft: string;
  draftAttachments: File[];
  replyToMessage: ChatMessage | null;
  pinnedMessages: ChatMessage[];
  composerFocusRequestId: number;
  onDraftChange: (v: string) => void;
  onSend: () => void;
  onAttachFiles: (files: File[]) => void;
  onRemoveAttachment: (index: number) => void;
  onRetryMessage: (message: ChatMessage) => void | Promise<void>;
  onReplyMessage: (message: ChatMessage) => void;
  onClearReply: () => void;
  onTogglePinMessage?: (message: ChatMessage) => void | Promise<void>;
  canPinMessages?: boolean;
  onUnpinPinnedMessage: (message: ChatMessage) => void | Promise<void>;
  isPinningMessage?: boolean;
  onForwardMessage: (message: ChatMessage) => void;
  onReportMessage: (message: ChatMessage) => void;
  onOpenChatDetails: () => void;
  canOpenSettings?: boolean;
  onOpenSettings?: () => void | Promise<void>;
  ensureMenuReady?: () => Promise<void>;
  canLeave?: boolean;
  isLeavingChat?: boolean;
  onLeaveChat?: () => void | Promise<void>;
  canToggleChatStatus?: boolean;
  isTogglingChatStatus?: boolean;
  onToggleChatStatus?: () => void | Promise<void>;
  canDeleteChat?: boolean;
  isDeletingChat?: boolean;
  onDeleteChat?: () => void | Promise<void>;
  canSendMessages?: boolean;
  composerPlaceholder?: string;
  isArchived?: boolean;
  isTogglingArchive?: boolean;
  onToggleArchive?: () => void | Promise<void>;
  hasOlderMessages?: boolean;
  isLoadingOlderMessages?: boolean;
  onLoadOlderMessages?: () => void | Promise<void>;
  groupParticipantsCount?: number;
};

export const ChatConversation = memo(
  ({
    thread,
    messages,
    draft,
    draftAttachments,
    replyToMessage,
    pinnedMessages,
    composerFocusRequestId,
    onDraftChange,
    onSend,
    onAttachFiles,
    onRemoveAttachment,
    onRetryMessage,
    onReplyMessage,
    onClearReply,
    onTogglePinMessage,
    canPinMessages = false,
    onUnpinPinnedMessage,
    isPinningMessage = false,
    onForwardMessage,
    onReportMessage,
    onOpenChatDetails,
    canOpenSettings = false,
    onOpenSettings,
    ensureMenuReady,
    canLeave = false,
    isLeavingChat = false,
    onLeaveChat,
    canToggleChatStatus = false,
    isTogglingChatStatus = false,
    onToggleChatStatus,
    canDeleteChat = false,
    isDeletingChat = false,
    onDeleteChat,
    canSendMessages = true,
    composerPlaceholder = 'Write a message...',
    isArchived = false,
    isTogglingArchive = false,
    onToggleArchive,
    hasOlderMessages = false,
    isLoadingOlderMessages = false,
    onLoadOlderMessages,
    groupParticipantsCount,
  }: ChatConversationProps) => {
    const listRef = useRef<HTMLDivElement>(null);
    const messageNodeMapRef = useRef<Record<number, HTMLDivElement | null>>({});
    const highlightTimeoutRef = useRef<number | null>(null);
    const [highlightedMessageId, setHighlightedMessageId] = useState<number | null>(null);
    const [viewerAttachment, setViewerAttachment] = useState<ChatAttachment | null>(null);
    const lastMessageId = messages.length ? messages[messages.length - 1].id : undefined;
    const prevThreadIdRef = useRef<number | null>(null);
    const prevLastMessageIdRef = useRef<number | undefined>(undefined);

    const handleLoadOlder = useCallback(async () => {
      const el = listRef.current;
      const prevScrollHeight = el?.scrollHeight ?? 0;

      await onLoadOlderMessages?.();

      requestAnimationFrame(() => {
        if (!el) {
          return;
        }

        el.scrollTop = el.scrollHeight - prevScrollHeight;
      });
    }, [onLoadOlderMessages]);

    useNearScrollTop(listRef, () => void handleLoadOlder(), {
      thresholdPx: CHAT_MESSAGES_SCROLL_TOP_THRESHOLD_PX,
      disabled: !hasOlderMessages || isLoadingOlderMessages || !onLoadOlderMessages,
    });

    useLayoutEffect(() => {
      if (!thread) return;
      const el = listRef.current;
      if (!el) return;

      const threadChanged = prevThreadIdRef.current !== thread.id;
      const grewAtEnd = lastMessageId !== prevLastMessageIdRef.current;

      prevThreadIdRef.current = thread.id;
      prevLastMessageIdRef.current = lastMessageId;

      if (!threadChanged && !grewAtEnd) {
        return;
      }

      let alive = true;
      const scrollToEnd = () => {
        if (!alive) return;
        el.scrollTop = el.scrollHeight;
      };
      scrollToEnd();
      requestAnimationFrame(() => {
        requestAnimationFrame(scrollToEnd);
      });
      return () => {
        alive = false;
      };
    }, [thread, lastMessageId]);

    useEffect(() => () => {
        if (highlightTimeoutRef.current) {
          window.clearTimeout(highlightTimeoutRef.current);
        }
      }, []);

    useEffect(() => {
      setViewerAttachment(null);
    }, [thread?.id]);

    const setMessageNode = useCallback((messageId: number, node: HTMLDivElement | null) => {
      if (node) {
        messageNodeMapRef.current[messageId] = node;
        return;
      }

      delete messageNodeMapRef.current[messageId];
    }, []);

    const scrollToReplyMessage = useCallback((messageId: number) => {
      const node = messageNodeMapRef.current[messageId];
      if (!node) return;

      node.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });

      setHighlightedMessageId(messageId);

      if (highlightTimeoutRef.current) {
        window.clearTimeout(highlightTimeoutRef.current);
      }

      highlightTimeoutRef.current = window.setTimeout(() => {
        setHighlightedMessageId((current) => (current === messageId ? null : current));
      }, 1800);
    }, []);

    const openAttachmentViewer = useCallback((attachment: ChatAttachment) => {
      if (attachment.kind !== 'image' && attachment.kind !== 'video') return;
      setViewerAttachment(attachment);
    }, []);

    const closeAttachmentViewer = useCallback(() => {
      setViewerAttachment(null);
    }, []);

    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const openRequestIdRef = useRef(0);

    if (!thread) {
      return (
        <section className="flex min-h-[420px] min-w-0 flex-1 flex-col items-center justify-center bg-[#F5F5F5] px-6 text-center">
          <p className="text-small font-medium text-default-600">Select a conversation</p>
          <p className="mt-1 max-w-xs text-tiny text-default-400">
            Choose a chat from the list to open messages.
          </p>
        </section>
      );
    }

    return (
      <section className="flex min-h-0 min-w-0 flex-1 flex-col bg-default-50">
        <header className="flex shrink-0 items-center gap-3 bg-content1 px-4 py-3">
          <div className="relative shrink-0">
            <Avatar name={thread.title} src={thread.avatarUrl} size="md" showFallback />
            {thread.kind === 'personal' && thread.online ? (
              <span
                className="absolute right-0 bottom-0 size-2.5 rounded-full border-2 border-white bg-success"
                aria-hidden
              />
            ) : null}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-small font-semibold text-foreground">{thread.title}</p>
            {thread.kind === 'group' ? (
              <div className="min-h-[17px]">
                {groupParticipantsCount != null ? (
                  <p className="truncate text-[14px] font-normal leading-[120%] tracking-[0] text-grey-description">
                    {groupParticipantsCount} participants
                  </p>
                ) : null}
              </div>
            ) : null}
            {thread.kind === 'personal' ? (
              <div className="min-h-4">
                {thread.lastSeenLabel ? (
                  <p
                    className={cn(
                      'truncate text-tiny',
                      thread.online ? 'font-medium text-success' : 'text-default-500',
                    )}
                  >
                    {thread.lastSeenLabel}
                  </p>
                ) : null}
              </div>
            ) : null}
          </div>
          <Dropdown
            placement="bottom-end"
            isOpen={isMenuOpen}
            onOpenChange={(open) => {
              if (!open) {
                setIsMenuOpen(false);
                return;
              }

              const requestId = ++openRequestIdRef.current;
              setIsMenuOpen(false);

              void (async () => {
                await ensureMenuReady?.();
                if (requestId !== openRequestIdRef.current) {
                  return;
                }
                setIsMenuOpen(true);
              })();
            }}
          >
            <DropdownTrigger>
              <Button
                isIconOnly
                variant="light"
                radius="full"
                aria-label="Chat actions"
              >
                <IconKebab className="text-default-500" />
              </Button>
            </DropdownTrigger>
            <DropdownMenu
              aria-label="Conversation actions"
              itemClasses={{
                base: 'rounded-[12px] px-3 py-2 text-[#1D1D1D] data-[hover=true]:bg-black/[0.04]',
                title: 'text-[14px] font-normal leading-[120%] tracking-[0]',
              }}
            >
              <DropdownItem
                key="details"
                startContent={<IconInfoCircle className="h-[18px] w-[18px] text-grey-description" />}
                onPress={onOpenChatDetails}
              >
                {thread.kind === 'group' ? 'Group details' : 'Chat information'}
              </DropdownItem>
              {canOpenSettings && onOpenSettings ? (
                <DropdownItem
                  key="settings"
                  startContent={<IconSettings className="h-[18px] w-[18px] text-grey-description" />}
                  onPress={() => void onOpenSettings()}
                >
                  Setting
                </DropdownItem>
              ) : null}
              {onToggleArchive ? (
                <DropdownItem
                  key="archive"
                  startContent={<IconArchive className="h-[18px] w-[18px] text-grey-description" />}
                  isDisabled={isTogglingArchive}
                  onPress={() => void onToggleArchive()}
                >
                  {isTogglingArchive
                    ? 'Updating...'
                    : isArchived
                      ? 'Unarchive chat'
                      : 'Archive chat'}
                </DropdownItem>
              ) : null}
              {canLeave && onLeaveChat ? (
                <DropdownItem
                  key="leave"
                  startContent={<IconLeave className="h-[18px] w-[18px] text-grey-description" />}
                  isDisabled={isLeavingChat}
                  onPress={() => void onLeaveChat()}
                >
                  {isLeavingChat ? 'Leaving...' : 'Leave chat'}
                </DropdownItem>
              ) : null}
              {canToggleChatStatus && onToggleChatStatus ? (
                <DropdownItem
                  key="toggle-status"
                  startContent={
                    <IconLock
                      className={cn(
                        'h-[18px] w-[18px]',
                        thread.status === 'closed' ? 'text-danger' : 'text-grey-description',
                      )}
                    />
                  }
                  isDisabled={isTogglingChatStatus}
                  onPress={() => void onToggleChatStatus()}
                >
                  {isTogglingChatStatus
                    ? 'Updating...'
                    : thread.status === 'closed'
                      ? 'Open chat'
                      : 'Close chat'}
                </DropdownItem>
              ) : null}
              {canDeleteChat && onDeleteChat ? (
                <DropdownItem
                  key="delete"
                  startContent={<IconTrash className="h-[18px] w-[18px] text-danger" />}
                  className="text-danger"
                  isDisabled={isDeletingChat}
                  onPress={() => void onDeleteChat()}
                >
                  {isDeletingChat ? 'Deleting...' : 'Delete chat'}
                </DropdownItem>
              ) : null}
            </DropdownMenu>
          </Dropdown>
        </header>

        <div className="relative min-h-0 min-w-0 flex-1">
          <div
            ref={listRef}
            className={`h-full overflow-y-auto overscroll-y-contain px-4 pb-4 ${pinnedMessages.length > 0 ? 'pt-0' : 'pt-4'}`}
          >
            {pinnedMessages.length > 0 ? (
              <div className="sticky top-0 z-30 -mx-4 border-b border-black/[0.08] bg-[rgba(245,245,245,0.52)] py-0 shadow-[0_8px_24px_-12px_rgba(0,0,0,0.12)] backdrop-blur-[20px] backdrop-saturate-150 supports-[backdrop-filter]:bg-[rgba(245,245,245,0.38)]">
                <ChatPinnedMessages
                  messages={pinnedMessages}
                  canUnpin={canPinMessages}
                  isPinning={isPinningMessage}
                  onUnpinMessage={onUnpinPinnedMessage}
                  onGoToMessage={scrollToReplyMessage}
                  embedded
                />
              </div>
            ) : null}
            {isLoadingOlderMessages ? (
              <div className="flex justify-center py-2">
                <Spinner size="sm" color="default" />
              </div>
            ) : null}
            {/* Живая область: сообщения приходят без действия пользователя,
                без role="log" скринридер о них молчит */}
            <div role="log" aria-live="polite" aria-label="Messages" className="space-y-3">
              {messages.map((msg) => (
                <div
                  key={msg.clientMessageId ?? `message-${msg.id}`}
                  ref={(node) => setMessageNode(msg.id, node)}
                  className="scroll-mt-24"
                >
                  <MessageBubble
                    message={msg}
                    onRetry={msg.failed ? onRetryMessage : undefined}
                    onOpenAttachment={openAttachmentViewer}
                    onReply={msg.kind === 'user' ? onReplyMessage : undefined}
                    onTogglePin={
                      canPinMessages && msg.kind === 'user' ? onTogglePinMessage : undefined
                    }
                    onForward={msg.kind === 'user' ? onForwardMessage : undefined}
                    onReport={!msg.isOwn && msg.kind === 'user' ? onReportMessage : undefined}
                    showAuthorName={thread.kind === 'group'}
                    isPinned={isMessagePinned(pinnedMessages, msg.id)}
                    isHighlighted={highlightedMessageId === msg.id}
                    onReplyPreviewClick={scrollToReplyMessage}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        {canSendMessages ? (
          <ChatComposer
            value={draft}
            attachments={draftAttachments}
            replyToMessage={replyToMessage}
            focusRequestId={composerFocusRequestId}
            onChange={onDraftChange}
            onSend={onSend}
            onAttachFiles={onAttachFiles}
            onRemoveAttachment={onRemoveAttachment}
            onClearReply={onClearReply}
            placeholder={composerPlaceholder}
          />
        ) : (
          <div className="shrink-0 border-t border-default-200 bg-content1 px-4 py-3 text-center text-sm text-default-500">
            {composerPlaceholder}
          </div>
        )}
        <ChatMediaViewerModal
          isOpen={!!viewerAttachment}
          attachment={viewerAttachment}
          onClose={closeAttachmentViewer}
        />
      </section>
    );
  },
);

ChatConversation.displayName = 'ChatConversation';
